import dayjs from 'dayjs';

/**
 * Generate a unique ID with a prefix
 * @param {string} prefix - The prefix for the ID (e.g., 'portfolio', 'pot', 'txn')
 * @returns {string} Unique ID
 */
export const generateId = (prefix) => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get current date in standard format (YYYY-MM-DD)
 * @returns {string} Current date
 */
export const getCurrentDate = () => {
  return dayjs().format('YYYY-MM-DD');
};

/**
 * Check if a date string represents today
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is today
 */
export const isToday = (dateStr) => {
  return dayjs(dateStr).isSame(dayjs(), 'day');
};

/**
 * Compare two dates for equality (same day)
 * @param {string} date1Str - First date string
 * @param {string} date2Str - Second date string
 * @returns {boolean} True if dates are the same day
 */
export const isSameDate = (date1Str, date2Str) => {
  return dayjs(date1Str).isSame(dayjs(date2Str), 'day');
};

/**
 * Format a date string to display format (DD/MM/YYYY)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date
 */
export const formatDisplayDate = (dateStr) => {
  return dayjs(dateStr).format('DD/MM/YYYY');
};

/**
 * Parse a date from various formats to YYYY-MM-DD
 * @param {string} dateStr - Date string in any common format
 * @param {string[]} formats - Array of formats to try
 * @returns {string|null} Parsed date in YYYY-MM-DD format, or null if invalid
 */
export const parseDate = (dateStr, formats = ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY']) => {
  for (const format of formats) {
    if (dayjs(dateStr, format, true).isValid()) {
      return dayjs(dateStr, format).format('YYYY-MM-DD');
    }
  }
  return null;
};

/**
 * Round a number to specified decimal places
 * @param {number} value - Number to round
 * @param {number} decimals - Number of decimal places
 * @returns {number} Rounded number
 */
export const roundTo = (value, decimals = 2) => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

/**
 * Check if two numbers are equal within a tolerance
 * @param {number} a - First number
 * @param {number} b - Second number
 * @param {number} tolerance - Tolerance (default 0.01)
 * @returns {boolean} True if numbers are equal within tolerance
 */
export const numbersEqual = (a, b, tolerance = 0.01) => {
  return Math.abs(a - b) < tolerance;
};
